import { AxiosResponse } from 'axios'
import { http } from '../realisations/Http.realisation'
import { UpdateAdditionalServicesPayloadT } from '../types/additional_services.types'

import {
    FTLOrderPayloadI,
    FTLOrdersGetListParamsT,
    FTLOrdersGetListResponseT,
    GetFTLOrderResponseT,
    GetInvoiceTripsInfo,
    OrderPayloadI,
    OrdersDownloadFTLPayloadT,
    OrdersDownloadFTLResponseT,
    OrdersGetInvoiceForPrintResponseT,
    OrdersGetInvoiceHistoryResponseT,
    OrdersGetInvoiceResponseT,
    OrdersGetInvoiceStatusesT,
    OrdersGetListParamsT,
    OrdersGetListResponseT,
    OrdersGetOrderForPrintResponseT,
    OrdersGetOrderInvoicesResponseT,
    OrdersGetOrderResponseT,
    OrdersGetPreviousStatuses,
    RestoreInvoicePayload,
    SendDispatcherCommentInvoiceT,
    SendDispatcherCommentOrderT,
    SetDeclaredPricePayloadT,
    setNewPredictDateParams,
    UpdateOrderPayloadI,
    GetInvoiceCouriersResponseT,
    CallcenterOrderPayload,
    GetFtlOrderDelayResponse,
    GetCourierAppDeliveryCancelReasonsResponse,
    ArrestInvoicePayload,
} from '../types/orders.types'

import {
    DownloadInsuranceResponse,
    GetCostFilesT,
    GetInvoiceProblemsT,
    GetInvoiceStatusHistory,
    GetPeriodsT,
    GetShortcomingFilesT,
    GetTemperatureRegimesT,
} from '../types/create_order.types'

export class OrdersRepository {
    getList(params?: OrdersGetListParamsT): Promise<AxiosResponse<OrdersGetListResponseT>> {
        return http.get('/cabinet/api/v2/admin/orders', { params })
    }

    getFTLOrders(
        params?: FTLOrdersGetListParamsT
    ): Promise<AxiosResponse<FTLOrdersGetListResponseT>> {
        return http.get('/cabinet/api/v2/admin/ftl-orders', { params })
    }

    getOrder(order_id: number): Promise<AxiosResponse<OrdersGetOrderResponseT>> {
        return http.get(`/cabinet/api/v2/orders/${order_id}`)
    }

    getFTLOrder(order_id: number): Promise<AxiosResponse<GetFTLOrderResponseT>> {
        return http.get(`/cabinet/api/v2/ftl-order/${order_id}`)
    }

    getFtlOrderDelay(order_id: number): Promise<AxiosResponse<GetFtlOrderDelayResponse>> {
        return http.get(`/cabinet/api/v2/ftl/order/${order_id}/get-delay`)
    }

    setCommentFtlOrder(
        order_id: number,
        commentId: number,
        stateId: number
    ): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/ftl/order/${order_id}/set-comment`, {
            commentId,
            stateId,
        })
    }

    getOrderInvoices(
        order_id: number,
        params: any
    ): Promise<AxiosResponse<OrdersGetOrderInvoicesResponseT>> {
        return http.get(`/cabinet/api/v2/logistics-info/order/${order_id}`, { params })
    }

    getOrderInvoicesForPrint(
        order_id: number
    ): Promise<AxiosResponse<OrdersGetOrderInvoicesResponseT>> {
        return http.get(`/cabinet/api/v2/logistics-info/order/print/${order_id}`)
    }

    getOrderForPrint(order_id: number): Promise<AxiosResponse<OrdersGetOrderForPrintResponseT>> {
        return http.get(`/cabinet/api/invoices/${order_id}`)
    }

    getInvoice(invoice_id: number): Promise<AxiosResponse<OrdersGetInvoiceResponseT>> {
        return http.get(`/cabinet/api/v2/logistics-info/${invoice_id}`)
    }

    getInvoiceTripsInfo(invoice_id: number): Promise<AxiosResponse<GetInvoiceTripsInfo>> {
        return http.get(`/tms/api/ttn-invoices/${invoice_id}`)
    }

    getLTLInvoiceForPrint(invoice_id: number): Promise<AxiosResponse<OrdersGetInvoiceResponseT>> {
        return http.get(`/cabinet/api/v2/logistics-info/print/${invoice_id}`)
    }

    getInvoiceByInvoiceNumber(
        invoice_number: number
    ): Promise<AxiosResponse<OrdersGetInvoiceResponseT>> {
        return http.get(`/cabinet/api/v2/logistics-info/invoice-number/${invoice_number}`)
    }

    updateInvoiceNumber(invoice_id: number, invoice_number: string): Promise<AxiosResponse<void>> {
        return http.put('/cabinet/api/admin/invoices/change-invoice-number', {
            invoiceId: invoice_id,
            newInvoiceNumber: invoice_number,
        })
    }

    setNewPredictDate(
        invoice_id: number,
        params: setNewPredictDateParams
    ): Promise<AxiosResponse<void>> {
        return http.put(
            `/cabinet/api/ltl-invoice-status/${invoice_id}/set-new-predict-date`,
            params
        )
    }

    verify(invoice_id: number, verify: number): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/admin/invoices/${invoice_id}/verify`, { verify })
    }

    setPreviousStatus(invoice_id: number, code: string): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/admin/invoices/${invoice_id}/set-previous-status`, { code })
    }

    getInvoiceForPrint(
        invoice_number: string
    ): Promise<AxiosResponse<OrdersGetInvoiceForPrintResponseT>> {
        return http.get(`/cabinet/api/pdf/${invoice_number}`)
    }

    getInvoiceDeliveryHistory(
        invoice_number: string
    ): Promise<AxiosResponse<OrdersGetInvoiceHistoryResponseT>> {
        return http.get(`/cabinet/api/invoice-status/${invoice_number}`)
    }

    cancelOrder(order_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/orders/${order_id}/cancel`, null)
    }

    sendCommentOrder(payload: SendDispatcherCommentOrderT): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/orders/send-dispatcher-notification`, payload)
    }

    importActNumbers(payload: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(`/cabinet/api/v2/admin/ftl-orders/import-act-numbers`, payload)
    }

    sendCommentInvoice(payload: SendDispatcherCommentInvoiceT): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/notifications/send-wait-list-message`, payload)
    }

    cancelInvoice(invoice_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/logistics-info/${invoice_id}/cancel`, null)
    }

    arrestInvoice(invoice_id: number, payload: ArrestInvoicePayload): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/invoices/${invoice_id}/arrest`, payload)
    }

    approveSelfDelivery(invoice_id: number): Promise<AxiosResponse<void>> {
        return http.post(`/dispatcher/api/invoices/${invoice_id}/approve-self-delivery`, {
            id: invoice_id,
        })
    }

    approveDelivery(invoice_id: number, payload: FormData): Promise<AxiosResponse<void>> {
        return http.postFormData(`/dispatcher/api/invoices/${invoice_id}/approve-delivery`, payload)
    }

    restoreInvoice(
        invoice_id: number,
        payload: RestoreInvoicePayload | undefined
    ): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/logistics-info/${invoice_id}/restore`, payload)
    }

    createOrder(payload: OrderPayloadI): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/orders', payload)
    }

    updateOrder(order_id: number, payload: UpdateOrderPayloadI): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/v2/orders/${order_id}/invoice`, payload)
    }

    createFTLOrder(payload: FTLOrderPayloadI): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/ftl-order', payload)
    }

    createCallcenterOrder(payload: CallcenterOrderPayload): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/call-center/orders', payload)
    }

    getInvoiceStatuses(invoice_number: string): Promise<AxiosResponse<OrdersGetInvoiceStatusesT>> {
        return http.get(`/cabinet/api/order-statuses/${invoice_number}/invoice-number`)
    }

    getPreviousStatuses(): Promise<AxiosResponse<OrdersGetPreviousStatuses>> {
        return http.get(`/cabinet/api/ref-statuses`, { params: { isActive: true } })
    }

    setDeclaredPrice(id: number, payload: SetDeclaredPricePayloadT): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/admin/invoices/${id}/set-declared-price`, payload)
    }

    updateOrderAdditionalServices(
        id: number,
        payload: UpdateAdditionalServicesPayloadT
    ): Promise<AxiosResponse<void>> {
        return http.put(`/cabinet/api/v2/additional-services/${id}`, payload)
    }

    createOrderFromExcel(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/orders/excel', payload)
    }

    createMultipleOrderFromExcel(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/orders/excel/multiple', payload)
    }

    createMultipleFTLOrderFromExcel(payload: FormData): Promise<AxiosResponse<void>> {
        return http.post('/cabinet/api/v2/ftl-order/import', payload)
    }

    exportExcelFTL(params: OrdersDownloadFTLPayloadT): Promise<AxiosResponse<Blob>> {
        return http.get('/cabinet/api/v2/ftl/order/export', {
            responseType: 'blob',
            params,
        })
    }

    exportExcelCompanyFTL(
        params: OrdersDownloadFTLPayloadT
    ): Promise<AxiosResponse<OrdersDownloadFTLResponseT>> {
        return http.get('/cabinet/api/v2/ftl/order/export-by-company', {
            responseType: 'blob',
            params: { ...params },
        })
    }

    getTemperatureRegimes(): Promise<AxiosResponse<GetTemperatureRegimesT>> {
        return http.get('/cabinet/api/temperature-regime-types')
    }

    getPeriods(): Promise<AxiosResponse<GetPeriodsT>> {
        return http.get('/cabinet/api/order-periods')
    }

    getInvoiceProblems(invoiceId: number): Promise<AxiosResponse<GetInvoiceProblemsT>> {
        return http.get(`/dispatcher/api/invoices/${invoiceId}/problems`)
    }

    getInvoiceStatusHistory(
        invoiceNumber: string
    ): Promise<AxiosResponse<GetInvoiceStatusHistory>> {
        return http.get(`/cabinet/api/order-statuses/${invoiceNumber}/history`)
    }

    saveBox(invoiceId: number, packCode: string): Promise<AxiosResponse<void>> {
        return http.post(`/cabinet/api/courier-app/take_info/${invoiceId}/save-pack-code`, {
            packCode,
        })
    }

    getShortcomingFiles(orderId: number): Promise<AxiosResponse<GetShortcomingFilesT>> {
        return http.get(`/dispatcher/api/courier-app/order-take/shortcoming-files/${orderId}`)
    }

    getCostFilesDeliveries(invoiceId: number): Promise<AxiosResponse<GetCostFilesT>> {
        return http.get(`/dispatcher/api/courier-app/delivery/courier-payment/${invoiceId}`)
    }

    getCostFilesTakes(orderId: number): Promise<AxiosResponse<GetCostFilesT>> {
        return http.get(`/dispatcher/api/courier-app/order-take/courier-payment/${orderId}`)
    }

    downloadInsurance(fileUuid: string): Promise<AxiosResponse<DownloadInsuranceResponse>> {
        return http.get(`/cabinet/api/file/download/${fileUuid}`, {
            responseType: 'blob',
        })
    }

    resendInvoiceDeliveryStatusToOneC(invoiceId: number): Promise<AxiosResponse<void>> {
        return http.post(`/dispatcher/api/invoices/${invoiceId}/resend-status-onec`, {})
    }

    getInvoiceCouriers(invoiceId: number): Promise<AxiosResponse<GetInvoiceCouriersResponseT>> {
        return http.get(`/dispatcher/api/invoices/${invoiceId}/courier`)
    }

    getCancelReasons(companyId: number): Promise<AxiosResponse<GetCourierAppDeliveryCancelReasonsResponse>> {
        return http.get(`/dispatcher/api/courier-app/delivery/${companyId}/cancel-reason`)
    }

    updateProperties(invoiceId: number): Promise<AxiosResponse<void>> {
        return http.put(`/dispatcher/api/invoices/${invoiceId}/properties`, {})
    }
}
